/**
 * Wither skulls (vanilla WitherSkull). DOM-free.
 *  - black skull: accelerates along its aim at 0.1/tick with 0.95 inertia (terminal ~1.9 blocks/tick);
 *    blue ("dangerous", 0.1% of the centre head's shots): inertia 0.73, so it crawls (~0.27) but its
 *    blast ignores the blast resistance of every breakable block (vanilla caps it at 0.8).
 *  - direct hit: 8 damage (difficulty-scaled for players) + Wither II (easy none, normal 10 s, hard 40 s);
 *    a wither that kills with it heals 5. Impact: explosion of power 1, no fire.
 *  - not pickable: arrows and punches pass through it; it never hurts its owner or another wither.
 */

import { AIR, BLAST_RESISTANCE, IS_LAVA, IS_SOLID, IS_WATERY } from '../../blocks';
import { difficultyDamage } from '../../game/combat';
import { FX, giveMobEffect } from '../../game/effects/effects';
import type { World } from '../../world/chunk';
import type { PlayerTarget } from '../host';
import type { Mob } from '../mob';

export interface WitherSkull {
  id: number;
  blue: boolean;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  ax: number; ay: number; az: number;
  vx: number; vy: number; vz: number;
  age: number;
  /** Shooter mob id. */
  owner: number;
  removed: boolean;
}

export interface SkullHost {
  world: World;
  players(): readonly PlayerTarget[];
  mobs(): readonly Mob[];
  /** Hurt a mob (EntityManager.attack: knockback, drops, XP). */
  hurtMob(m: Mob, amount: number, fromX: number, fromZ: number): void;
  /** Explosion: blocks + area damage. */
  explode(x: number, y: number, z: number, power: number): void;
  /** Remove a block for the blue skull's blast (false = protected). */
  breakBlock?(x: number, y: number, z: number): boolean;
  /** Give a hit player the Wither effect (blocked hits never call this). */
  playerEffect?(p: PlayerTarget, id: number, amp: number, dur: number): void;
  difficulty?(): 'peaceful' | 'easy' | 'normal' | 'hard';
}

export const SKULL_DAMAGE = 8;
const MAX_AGE = 600;
const RADIUS = 0.15625; // 0.3125 box
/** Wither effect duration on a hit (vanilla: none on easy, 10 s normal, 40 s hard). */
export const witherEffectTicks = (d: 'peaceful' | 'easy' | 'normal' | 'hard'): number => (d === 'normal' ? 200 : d === 'hard' ? 800 : 0);

export class WitherSkulls {
  readonly list: WitherSkull[] = [];
  /** Fired when a skull is launched (sound). */
  onShoot: ((s: WitherSkull) => void) | null = null;
  private nextId = 1;

  spawn(blue: boolean, x: number, y: number, z: number, dx: number, dy: number, dz: number, owner: number): WitherSkull {
    const l = Math.hypot(dx, dy, dz) || 1;
    const s: WitherSkull = {
      id: this.nextId++, blue, x, y, z, px: x, py: y, pz: z,
      ax: (dx / l) * 0.1, ay: (dy / l) * 0.1, az: (dz / l) * 0.1, vx: 0, vy: 0, vz: 0,
      age: 0, owner, removed: false,
    };
    this.list.push(s);
    this.onShoot?.(s);
    return s;
  }

  clear(): void { this.list.length = 0; }

  tick(h: SkullHost): void {
    for (const s of this.list) {
      if (s.removed) continue;
      s.px = s.x; s.py = s.y; s.pz = s.z;
      if (++s.age > MAX_AGE) { s.removed = true; continue; }
      const k = s.blue ? 0.73 : 0.95;
      s.vx = (s.vx + s.ax) * k; s.vy = (s.vy + s.ay) * k; s.vz = (s.vz + s.az) * k;
      // march in small steps so a fast skull never skips a wall or an entity
      const steps = 4;
      for (let i = 0; i < steps && !s.removed; i++) {
        s.x += s.vx / steps; s.y += s.vy / steps; s.z += s.vz / steps;
        this.collide(s, h);
      }
    }
    let w = 0;
    for (const s of this.list) if (!s.removed) this.list[w++] = s;
    this.list.length = w;
  }

  private collide(s: WitherSkull, h: SkullHost): void {
    const r = RADIUS;
    for (const p of h.players()) {
      if (!p.alive || p.creative || p.spectator || s.age < 2) continue;
      if (Math.abs(s.x - p.x) < p.width / 2 + r && Math.abs(s.z - p.z) < p.width / 2 + r && s.y > p.y - r && s.y < p.y + p.height + r) {
        const diff = h.difficulty?.() ?? 'normal';
        const res = p.damage(difficultyDamage(SKULL_DAMAGE, diff), { kind: 'projectile', x: s.x, y: s.y, z: s.z, attackerId: s.owner });
        if (res !== 'blocked') {
          const dur = witherEffectTicks(diff);
          if (dur > 0) h.playerEffect?.(p, FX.WITHER, 1, dur);
        }
        this.impact(s, h);
        return;
      }
    }
    for (const m of h.mobs()) {
      if (!m.alive || m.id === s.owner || m.type === 'wither') continue;
      if (Math.abs(s.x - m.x) < m.width / 2 + r && Math.abs(s.z - m.z) < m.width / 2 + r && s.y > m.y - r && s.y < m.y + m.height + r) {
        h.hurtMob(m, SKULL_DAMAGE, s.x - s.vx, s.z - s.vz);
        if (m.alive) giveMobEffect(m, FX.WITHER, 1, witherEffectTicks(h.difficulty?.() ?? 'normal') || 200);
        else {
          // vanilla: the shooter heals 5 when its skull kills
          const o = h.mobs().find((q) => q.id === s.owner);
          if (o && o.alive) o.health = Math.min(o.maxHealth, o.health + 5);
        }
        this.impact(s, h);
        return;
      }
    }
    if (IS_SOLID[h.world.getBlock(Math.floor(s.x), Math.floor(s.y), Math.floor(s.z))] === 1) this.impact(s, h);
  }

  private impact(s: WitherSkull, h: SkullHost): void {
    s.removed = true;
    h.explode(s.x, s.y, s.z, 1);
    if (s.blue && h.breakBlock) {
      // the blue skull's blast has its resistance capped at 0.8: every breakable block in its crater goes
      const cx = Math.floor(s.x), cy = Math.floor(s.y), cz = Math.floor(s.z);
      for (let dy = -1; dy <= 1; dy++) for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
        if (dx * dx + dy * dy + dz * dz > 2) continue;
        const id = h.world.getBlock(cx + dx, cy + dy, cz + dz);
        if (id === AIR || IS_WATERY[id] === 1 || IS_LAVA[id] === 1 || BLAST_RESISTANCE[id] === Number.POSITIVE_INFINITY) continue;
        h.breakBlock(cx + dx, cy + dy, cz + dz);
      }
    }
  }
}
